// noinspection DuplicatedCode
import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import Button from "@material-tailwind/react/Button";
import Modal from "@material-tailwind/react/Modal";
import ModalBody from "@material-tailwind/react/ModalBody";
import ModalFooter from "@material-tailwind/react/ModalFooter";
import ModalHeader from "@material-tailwind/react/ModalHeader";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {
  getJadwalKerja,
  importJadwalKerja,
  downloadTemplateJadwalKerja,
  deleteJadwalKerja,
  clearJadwalKerjaMessage,
  jadwalKerjaSelector,
} from "./jadwalKerjaSlice";

import { Colors } from "../../../../assets/Colors";
import Loading from "../../../../components/loading/Loading";

// Bantu konversi Date lokal -> "YYYY-MM-DD" tanpa lewat toISOString (yang
// menerjemahkan ke UTC dan mundur 1 hari untuk WIB) — jebakan yang sama
// pernah kejadian di AbsensiPage, lihat memori "halaman absensi AttLog".
const toLocalDateParam = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const JadwalKerjaPage = () => {
  const dispatch = useDispatch();

  const {
    data,
    pagination,
    isGetFetching,
    isImportFetching,
    isDownloadFetching,
    isDeleteFetching,
    message,
    errorMessage,
    importResult,
  } = useSelector(jadwalKerjaSelector);

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [dateRange, setDateRange] = useState({
    startDate: firstOfMonth,
    endDate: today,
  });
  const [nikFilter, setNikFilter] = useState("");
  const [showModalUpload, setShowModalUpload] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const fetchData = useCallback(
    (page = 1) => {
      dispatch(
        getJadwalKerja({
          nik: nikFilter || undefined,
          start_date: toLocalDateParam(dateRange.startDate),
          end_date: toLocalDateParam(dateRange.endDate),
          page,
          page_size: pagination.pageSize || 20,
        })
      ).finally(() => setIsInitialLoad(false));
    },
    [dispatch, nikFilter, dateRange, pagination.pageSize]
  );

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, nikFilter]);

  useEffect(() => {
    if (message) {
      toast.success(message, { position: "bottom-right", autoClose: 3000 });
      dispatch(clearJadwalKerjaMessage());
    }
    if (errorMessage) {
      toast.error(errorMessage, { position: "bottom-right", autoClose: 5000 });
      dispatch(clearJadwalKerjaMessage());
    }
  }, [message, errorMessage, dispatch]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validExtensions = [".xlsx", ".xls"];
    const fileExtension = file.name
      .toLowerCase()
      .slice(file.name.lastIndexOf("."));

    if (!validExtensions.includes(fileExtension)) {
      toast.error("Format file tidak didukung. Harus .xlsx atau .xls", {
        position: "bottom-right",
        autoClose: 5000,
      });
      setExcelFile(null);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File terlalu besar. Maksimal 10MB", {
        position: "bottom-right",
        autoClose: 5000,
      });
      setExcelFile(null);
      return;
    }

    setExcelFile(file);
  };

  const handleUploadExcel = async () => {
    if (!excelFile) {
      toast.error("Pilih file terlebih dahulu", {
        position: "bottom-right",
        autoClose: 5000,
      });
      return;
    }

    try {
      const result = await dispatch(importJadwalKerja(excelFile)).unwrap();
      toast.success(result?.message || "File berhasil diupload", {
        position: "bottom-right",
        autoClose: 3000,
      });
      setShowModalUpload(false);
      setExcelFile(null);
      fetchData(1);
    } catch (error) {
      toast.error(error?.message || "Gagal upload file", {
        position: "bottom-right",
        autoClose: 5000,
      });
    }
  };

  const handleDownloadTemplate = () => {
    dispatch(downloadTemplateJadwalKerja());
  };

  const handleDelete = async (id, nama, tanggal) => {
    if (!window.confirm(`Hapus jadwal ${nama} tanggal ${tanggal}?`)) return;
    try {
      await dispatch(deleteJadwalKerja(id)).unwrap();
      fetchData(pagination.currentPage);
    } catch (error) {
      toast.error(error?.message || "Gagal menghapus jadwal", {
        position: "bottom-right",
        autoClose: 5000,
      });
    }
  };

  const handlePrevPage = () => {
    if (pagination.hasPrev) fetchData(pagination.currentPage - 1);
  };
  const handleNextPage = () => {
    if (pagination.hasNext) fetchData(pagination.currentPage + 1);
  };

  if (isGetFetching && isInitialLoad) {
    return <Loading />;
  }

  return (
    <>
      <div className="md:ml-64">
        <div
          style={{ background: Colors.primary }}
          className="pt-14 pb-16 px-3 md:px-8 h-auto"
        >
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"></div>
          </div>
        </div>

        <div className="px-3 md:px-8 h-auto -mt-24">
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 px-4">
              <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded">
                <div className="rounded-t mb-0 px-4 py-3 border-0">
                  <div className="flex flex-wrap items-center">
                    <div className="relative w-full px-4 max-w-full flex-grow flex-1">
                      <h3 className="font-semibold text-lg text-blueGray-700">
                        Jadwal Kerja Karyawan
                      </h3>
                      <p className="text-sm text-blueGray-400 mb-2">
                        Baseline jam masuk/pulang per karyawan per hari —
                        dipakai untuk menghitung Terlambat &amp; Cepat Pulang
                        di halaman Absensi. Upload lewat Excel, boleh diulang
                        (baris yang sama Pin+Tanggal akan tertimpa).
                      </p>
                      <div className="flex flex-wrap gap-4 mt-2">
                        <div className="flex flex-col">
                          <label className="text-xs text-blueGray-400 mb-1">
                            Dari Tanggal
                          </label>
                          <DatePicker
                            selected={dateRange.startDate}
                            onChange={(date) =>
                              setDateRange((prev) => ({ ...prev, startDate: date }))
                            }
                            className="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full"
                            dateFormat="dd/MM/yyyy"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-xs text-blueGray-400 mb-1">
                            Sampai Tanggal
                          </label>
                          <DatePicker
                            selected={dateRange.endDate}
                            onChange={(date) =>
                              setDateRange((prev) => ({ ...prev, endDate: date }))
                            }
                            className="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full"
                            dateFormat="dd/MM/yyyy"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-xs text-blueGray-400 mb-1">
                            PIN Karyawan
                          </label>
                          <input
                            type="text"
                            value={nikFilter}
                            onChange={(e) => setNikFilter(e.target.value)}
                            placeholder="Semua"
                            className="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-40"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-4 lg:mt-0 px-4">
                      <Button
                        color="green"
                        buttonType="filled"
                        size="sm"
                        ripple="light"
                        onClick={() => setShowModalUpload(true)}
                      >
                        Upload Jadwal
                      </Button>
                      <Button
                        color="lightBlue"
                        buttonType="outline"
                        size="sm"
                        ripple="light"
                        onClick={() => fetchData(pagination.currentPage)}
                        disabled={isGetFetching}
                      >
                        {isGetFetching ? "Memuat..." : "Refresh"}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="block w-full overflow-x-auto">
                  <table className="items-center w-full bg-transparent border-collapse">
                    <thead>
                      <tr>
                        {[
                          "Tanggal",
                          "PIN",
                          "Nama Karyawan",
                          "Shift",
                          "Jam Masuk",
                          "Jam Pulang",
                          "Lintas Hari",
                          "Toleransi",
                          "Keterangan",
                          "Aksi",
                        ].map((h) => (
                          <th
                            key={h}
                            className="px-4 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.length === 0 ? (
                        <tr>
                          <td
                            colSpan={10}
                            className="text-center py-6 text-blueGray-400 text-sm"
                          >
                            {isGetFetching
                              ? "Memuat data..."
                              : "Belum ada jadwal kerja pada rentang ini."}
                          </td>
                        </tr>
                      ) : (
                        data.map((row) => (
                          <tr key={row.JadwalKerjaID}>
                            <td className="border-t-0 px-4 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                              {row.TanggalDisplay || row.Tanggal}
                            </td>
                            <td className="border-t-0 px-4 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                              {row.Pin}
                            </td>
                            <td className="border-t-0 px-4 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                              {row.NamaKaryawan}
                            </td>
                            <td className="border-t-0 px-4 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                              {row.NamaShift || "-"}
                            </td>
                            <td className="border-t-0 px-4 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                              {row.Libur || row.SedangCuti ? "-" : row.JamMasukJadwal}
                            </td>
                            <td className="border-t-0 px-4 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                              {row.Libur || row.SedangCuti ? "-" : row.JamPulangJadwal}
                            </td>
                            <td className="border-t-0 px-4 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                              {row.LintasHari ? (
                                <span className="bg-orange-200 text-orange-800 px-2 py-1 rounded text-xs">
                                  Ya
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="border-t-0 px-4 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                              {row.Libur || row.SedangCuti ? "-" : `${row.ToleransiMenit} mnt`}
                            </td>
                            <td className="border-t-0 px-4 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                              {/* SedangCuti berasal dari TCuti berstatus 99 (disetujui
                                  penuh) yg tanggalnya menimpa tanggal jadwal ini — murni
                                  tampilan, baris TJadwalKerja aslinya tak diubah. */}
                              {row.Libur ? (
                                <span className="bg-blueGray-200 text-blueGray-800 px-2 py-1 rounded text-xs">
                                  Libur
                                </span>
                              ) : row.SedangCuti ? (
                                <span className="bg-blue-200 text-blue-800 px-2 py-1 rounded text-xs">
                                  Cuti
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="border-t-0 px-4 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                              <button
                                className="text-red-600 hover:text-red-800 text-xs font-semibold"
                                disabled={isDeleteFetching}
                                onClick={() =>
                                  handleDelete(
                                    row.JadwalKerjaID,
                                    row.NamaKaryawan,
                                    row.TanggalDisplay || row.Tanggal
                                  )
                                }
                              >
                                Hapus
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-between items-center px-4 py-3">
                  <span className="text-xs text-blueGray-400">
                    Halaman {pagination.currentPage} dari{" "}
                    {pagination.totalPages || 1} — {pagination.totalCount} baris
                  </span>
                  <div className="flex gap-2">
                    <Button
                      color="lightBlue"
                      buttonType="outline"
                      size="sm"
                      onClick={handlePrevPage}
                      disabled={!pagination.hasPrev || isGetFetching}
                    >
                      ← Sebelumnya
                    </Button>
                    <Button
                      color="lightBlue"
                      buttonType="outline"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={!pagination.hasNext || isGetFetching}
                    >
                      Berikutnya →
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal size="sm" active={showModalUpload} toggler={() => setShowModalUpload(false)}>
        <ModalHeader toggler={() => setShowModalUpload(false)}>
          Upload Jadwal Kerja
        </ModalHeader>
        <ModalBody>
          <div className="flex flex-wrap -mx-3 mb-6">
            <div className="w-full px-3">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                Pilih File Excel
              </label>
              <input
                type="file"
                accept=".xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                onChange={handleFileUpload}
                className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white border-gray-500"
                disabled={isImportFetching}
              />
              <p className="text-gray-600 text-xs italic mt-2">
                Format: .xlsx, .xls (Maksimal 10MB). Kolom: PIN, Tanggal
                (DD-MM-YYYY), Shift, Jam Masuk, Jam Pulang, Toleransi Menit,
                Libur.
              </p>
            </div>
          </div>

          {excelFile && (
            <div className="flex flex-wrap -mx-3 mb-6">
              <div className="w-full px-3">
                <div className="bg-green-50 border border-green-200 rounded p-3">
                  <p className="text-green-800 text-sm">
                    File terpilih: <strong>{excelFile.name}</strong>
                  </p>
                </div>
              </div>
            </div>
          )}

          {importResult && (
            <div className="flex flex-wrap -mx-3 mb-6">
              <div className="w-full px-3">
                <div className="bg-blueGray-50 border border-blueGray-200 rounded p-3 text-xs">
                  <p>
                    Berhasil: {importResult.success_count ?? 0}, Gagal:{" "}
                    {importResult.error_count ?? 0}
                  </p>
                  {Array.isArray(importResult.errors) &&
                    importResult.errors.length > 0 && (
                      <ul className="list-disc pl-4 mt-2 text-red-700 max-h-32 overflow-y-auto">
                        {importResult.errors.map((msg, idx) => (
                          <li key={idx}>{msg}</li>
                        ))}
                      </ul>
                    )}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap -mx-3">
            <div className="w-full px-3">
              <Button
                color="lightBlue"
                buttonType="filled"
                size="regular"
                rounded={false}
                block={true}
                ripple="light"
                onClick={handleDownloadTemplate}
                disabled={isDownloadFetching}
              >
                {isDownloadFetching ? "Downloading..." : "Download Template Excel"}
              </Button>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="red"
            buttonType="link"
            onClick={() => setShowModalUpload(false)}
            ripple="dark"
            disabled={isImportFetching}
          >
            Batal
          </Button>
          <Button
            color="green"
            onClick={handleUploadExcel}
            disabled={!excelFile || isImportFetching}
            ripple="light"
          >
            {isImportFetching ? "Mengupload..." : "Upload Excel"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default JadwalKerjaPage;
